import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { UtilitiesService, RequestHelper } from "src/app/core/services/utilities.service";
import { environment } from "src/environments/environment";
import { FieldworkData } from "../models/fieldwork";
import { DestructionFormCreateRequest, DestructionFormFilter, DestructionFormListResponse, DestructionFormRequest, DestructionFormResponse, DestructionFormUpdateRequest } from "../models/destruction-form.model";
import { RequestOption } from "src/app/shared/model/common.model";
import { HttpParams } from "@angular/common/http";
import { RecyclingFormFilter } from "../models/recycling-form..model";

@Injectable({
  providedIn: "root"
})
export class EolService {
  public static readonly EOL_MODULE_BASE_URL = environment.portalURL;
  private static readonly DESTRUCTION_FORMS_URL = `${EolService.EOL_MODULE_BASE_URL}/eol/eol-forms/destruction-forms`;
  private static readonly RECYCLING_FORMS_URL = `${EolService.EOL_MODULE_BASE_URL}/eol/eol-forms/recycling-forms`;
  private requestHelper: RequestHelper;

  constructor(private utilService: UtilitiesService, private httpClient: HttpClient) {
    this.requestHelper = utilService.req;
  }

  retrieveFieldworkData(params: { projectName?: string; padNo?: string; turbineId?: string; component?: string } = {}): Observable<{ data: FieldworkData}> {
    return this.requestHelper.httpGet<{ data: FieldworkData}>(
      `${EolService.DESTRUCTION_FORMS_URL}/retrieveFieldworkData`,
      params
    );
  }

  getDestructionForms(filter: DestructionFormFilter = {}): Observable<{ data: DestructionFormListResponse }> {
    return this.requestHelper.httpGet<{ data: DestructionFormListResponse }>(
      EolService.DESTRUCTION_FORMS_URL,
      filter
    );
  }

  createDestructionForm(payload: DestructionFormCreateRequest): Observable<{ data: DestructionFormResponse }> {
    return this.requestHelper.httpPost<{ data: DestructionFormResponse }>(
      EolService.DESTRUCTION_FORMS_URL,
      payload
    );
  }

  getDestructionFormById(id: number): Observable<{ data: DestructionFormResponse }> {
    return this.requestHelper.httpGet<{ data: DestructionFormResponse }>(
      `${EolService.DESTRUCTION_FORMS_URL}/${id}`
    );
  }

  getDestructionFormByHeaderRecordId(headerRecordId: number): Observable<{ data: DestructionFormResponse[] }> {
    return this.requestHelper.httpGet<{ data: DestructionFormResponse[] }>(
      `${EolService.DESTRUCTION_FORMS_URL}/headerRecordIds/${headerRecordId}`
    );
  }

  updateDestructionForm(payload: DestructionFormUpdateRequest): Observable<{ data: DestructionFormResponse }> {
    return this.requestHelper.httpPut<{ data: DestructionFormResponse }>(
      EolService.DESTRUCTION_FORMS_URL,
      payload
    );
  }

  deleteDestructionForm(id: number): Observable<any> {
    return this.requestHelper.httpDelete<any>(
      `${EolService.DESTRUCTION_FORMS_URL}/${id}`
    );
  }

  submitDestructionForms(ids: number[]): Observable<any> {
    return this.requestHelper.httpPost<any>(
      `${EolService.DESTRUCTION_FORMS_URL}/submit`,
      ids
    );
  }

  exportDestructionForms(ids: number[]): Observable<ArrayBuffer> {
    // const params: any = {};
    // Object.keys(filter).forEach(key => {
    //   const value = (filter as any)[key];
    //   if (value !== undefined && value !== null && value !== '') {
    //     params[key] = value;
    //   }
    // });
    const url = EolService.DESTRUCTION_FORMS_URL + '/export';
    return this.httpClient.post(`${url}`, ids, {
      responseType: 'arraybuffer'
    });
  }

  downloadCertificate(certificateS3Key: string): Observable<ArrayBuffer> {
    return this.httpClient.get(
      `${EolService.EOL_MODULE_BASE_URL}/file-upload/download-file/${certificateS3Key}`,
      { responseType: 'arraybuffer' }
    );
  }
  
  // RECYCLING FORM
  submitRecyclingForms(ids: number[]): Observable<any> {
    return this.requestHelper.httpPost<any>(
      `${EolService.RECYCLING_FORMS_URL}/submit`,
      ids
    );
  }
  getRecyclingForms(filter: any): Observable<any> {
    const params: any = {};
    Object.keys(filter).forEach((key) => {
      const value = (filter as any)[key];
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== '') {
        params[key] = normalizedValue;
      }
    });
    return this.requestHelper.httpGet<any>(
      EolService.RECYCLING_FORMS_URL,
      params
    );
  }

  createRecyclingForm(payload: any): Observable<any> {
    return this.requestHelper.httpPost<any>(
      EolService.RECYCLING_FORMS_URL,
      payload
    );
  }

  getRecyclingFormById(id: number): Observable<any> {
    return this.requestHelper.httpGet<any>(
      `${EolService.RECYCLING_FORMS_URL}/${id}`
    );
  }

  updateRecyclingForm(payload: any): Observable<any> {
    return this.requestHelper.httpPut<any>(
      EolService.RECYCLING_FORMS_URL,
      payload
    );
  }

  deleteRecyclingForm(id: number): Observable<any> {
    return this.requestHelper.httpDelete<any>(
      `${EolService.RECYCLING_FORMS_URL}/${id}`
    );
  }

  // DROPDOWN APIs RECYCLING FORM
  getProjectNameDropdown(filter: any = {}): Observable<any> {
    return this.requestHelper.httpGet<any>(
      `${EolService.DESTRUCTION_FORMS_URL}/dropdown/project-names`,
      filter
    );
  }

  getComponentsDropdown(filter: any = {}): Observable<any> {
    return this.requestHelper.httpGet<any>(
      `${EolService.DESTRUCTION_FORMS_URL}/dropdown/components`,
      filter
    );
  }

  getProcessingMethodsDropdown(filter: any = {}): Observable<any> {
    return this.requestHelper.httpGet<any>(
      `${EolService.EOL_MODULE_BASE_URL}/eol/eol-setup/field-glass-forms/dropdown/processing-methods`,
      filter
    );
  }

  getSupplierNamesDropdown(filter: any = {}): Observable<any> {
    return this.requestHelper.httpGet<any>(
      `${EolService.EOL_MODULE_BASE_URL}/eol/eol-setup/supplier-forms/dropdown/supplier-names`,
      filter
    );
  }

  exportRecycling(ids: number[]): Observable<ArrayBuffer> {
    // const params: any = {};
    // Object.keys(filter).forEach((key) => {
    //   const value = (filter as any)[key];
    //   const normalizedValue = typeof value === 'string' ? value.trim() : value;
    //   if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== '') {
    //     params[key] = normalizedValue;
    //   }
    // });
    const url = EolService.RECYCLING_FORMS_URL + '/export';
    return this.httpClient.post(`${url}`, ids, {
      responseType: 'arraybuffer'
    });
  }
}
